import { Ticket, HelpCircle, Dice5, Trophy } from 'lucide-react';
import YellowRule from '@/components/brand/YellowRule';
import { CONTACT } from '@/lib/contact';

/**
 * HOW IT WORKS — standalone detail page.
 *
 * Theme: light cream panel, matching the homepage "How it works"
 * section so the two reads as one coherent brand surface. The dark
 * site chrome (header / footer) is bracketed by soft gradient
 * transition bands so the cream page emerges and recedes cleanly
 * — see /app/frontend/src/pages/HomePage.jsx for the sibling
 * implementation.
 *
 * Visual language on cream:
 *   - Headings / body text sit on var(--pd-bg) (deep racing green)
 *   - Soft-tint body text: var(--pd-bg)/70
 *   - Cards: bg-white/60 with a hairline var(--pd-bg)/15 border
 *   - Numbered badge: brass gold (var(--pd-gold))
 *   - Accent links / icon fills: var(--pd-gold) (brass reads much
 *     better than the acid accent-yellow on a light panel)
 */

const STEPS = [
  {
    n: '01',
    t: 'Choose your tickets',
    d: 'Pick the prize you want. Buy one ticket or ten. Every ticket is one entry. All ticket prices are shown up front in pounds.',
    Icon: Ticket,
  },
  {
    n: '02',
    t: 'Answer the question',
    d: 'A simple skill question keeps every competition compliant. You can retry until you get it right. No trick questions.',
    Icon: HelpCircle,
  },
  {
    n: '03',
    t: 'Draw day',
    d: 'On draw day, we freeze the entry list, generate a cryptographic seed, and use it to pick one ticket. The seed and full eligible list are published so anyone can re-run the maths themselves. An independent witness also observes the draw live and their name goes on the record.',
    Icon: Dice5,
  },
  {
    n: '04',
    t: 'You win',
    d: 'Winners are contacted by email and phone. Prizes ship within 14 days. Cash alternatives paid within 48 hours.',
    Icon: Trophy,
  },
];

const HowItWorksPage = () => (
  <div data-testid="how-it-works-page">
    {/* Top fade: racing-green → dusty sage → cream (mirrors the
        homepage "How it works" transition band). */}
    <div
      aria-hidden="true"
      className="h-12 md:h-20 w-full relative"
      style={{
        background:
          'linear-gradient(to bottom, var(--pd-bg) 0%, #2a3f33 35%, #a8aa9c 75%, var(--pd-cream) 100%)',
      }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 top-[62%] h-px w-20 md:w-28 bg-[color:var(--pd-gold)]/55" />
    </div>

    <section className="bg-[color:var(--pd-cream)] text-[color:var(--pd-bg)] py-10 md:py-20">
      <div className="container-pd">
        {/* INTRO ------------------------------------------------------ */}
        <div className="max-w-3xl">
          <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">
            How it works
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.9] mt-3">
            Honest. Transparent. Proper.
          </h1>
          <p className="mt-4 text-base md:text-lg text-[color:var(--pd-bg)]/70">
            Every competition on The Padel Draw is run under UK prize
            competition law. That means a real prize, a real draw, and a free
            postal entry route on every single one.
          </p>
          <p
            className="mt-3 md:mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--pd-gold)]"
            data-testid="hiw-motto"
          >
            Equal Odds, Equal Opportunity.
          </p>
        </div>

        {/* STEPS ------------------------------------------------------ */}
        <div
          className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5"
          data-testid="hiw-steps"
        >
          {STEPS.map(({ n, t, d, Icon }) => (
            <div
              key={n}
              className="bg-white/60 border border-[color:var(--pd-bg)]/15 rounded-2xl p-5 md:p-6 flex gap-4 md:gap-5"
              data-testid={`hiw-step-${n}`}
            >
              {/* Icon badge — on the cream panel, we invert: the badge
                  itself becomes a soft parchment with a gold icon,
                  rather than a dark tile with yellow icon, so the
                  whole page reads as one tone. */}
              <div
                className="shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[color:var(--pd-cream)] border border-[color:var(--pd-bg)]/15 flex items-center justify-center relative"
                aria-hidden="true"
              >
                <Icon
                  size={26}
                  className="text-[color:var(--pd-gold)]"
                  strokeWidth={1.6}
                />
                <span className="absolute -top-2 -right-2 font-mono text-[10px] tracking-[0.14em] bg-[color:var(--pd-gold)] text-[#14251c] rounded-md px-1.5 py-0.5">
                  {n}
                </span>
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-2xl md:text-3xl leading-tight">
                  {t}
                </h2>
                <p className="mt-2 text-sm md:text-base text-[color:var(--pd-bg)]/70">
                  {d}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Divider — gold hairline matches the brand token */}
        <YellowRule className="mt-10 md:mt-16 mb-8 md:mb-12" />

        {/* COMPLIANCE + AUDIT ---------------------------------------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div>
            <h3 className="font-display text-2xl md:text-3xl">
              Free postal entry
            </h3>
            <p className="mt-3 md:mt-4 text-sm md:text-base text-[color:var(--pd-bg)]/70">
              Every competition on this site can be entered for free by post.
              Send a plain postcard with your full name, address, email, phone
              and your answer to the skill question to:
            </p>
            <div className="mt-4 bg-white/60 border border-[color:var(--pd-bg)]/15 rounded-2xl p-4 font-mono text-sm leading-relaxed text-[color:var(--pd-bg)]">
              The Padel Draw Ltd
              <br />
              5 Brayford Square
              <br />
              London E1 0SG
            </div>
            <p className="mt-3 text-xs text-[color:var(--pd-bg)]/60">
              One envelope = one entry. Post as many separate envelopes as you
              like, up to any per-competition cap. Proof of posting is not
              proof of entry or receipt.
            </p>
          </div>
          <div>
            <h3 className="font-display text-2xl md:text-3xl">
              Check any draw yourself
            </h3>
            <p className="mt-3 md:mt-4 text-sm md:text-base text-[color:var(--pd-bg)]/70">
              After every draw we publish the seed, the full list of eligible
              ticket numbers, and the algorithm. Paste them into any Python
              interpreter and you&rsquo;ll get the same winning ticket we did.
              See{' '}
              <a
                href="/how-we-draw-winners"
                className="text-[color:var(--pd-gold)] underline underline-offset-4 decoration-[color:var(--pd-gold)]/40 hover:decoration-[color:var(--pd-gold)]"
              >
                How we draw winners
              </a>{' '}
              for the plain-English walkthrough, or{' '}
              <a
                href="/past-draws"
                className="text-[color:var(--pd-gold)] underline underline-offset-4 decoration-[color:var(--pd-gold)]/40 hover:decoration-[color:var(--pd-gold)]"
              >
                Past draws
              </a>{' '}
              for the live audit log.
            </p>
            <div className="mt-4 bg-white/60 border border-[color:var(--pd-bg)]/15 rounded-2xl p-4 text-xs text-[color:var(--pd-bg)]/70">
              Questions? Call us on{' '}
              <a
                href={CONTACT.phoneHref}
                className="text-[color:var(--pd-gold)] font-medium"
              >
                {CONTACT.phoneDisplay}
              </a>{' '}
              or email{' '}
              <a
                href={CONTACT.emailHref}
                className="text-[color:var(--pd-gold)] font-medium"
              >
                {CONTACT.email}
              </a>
              .
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Bottom fade: cream → dusty sage → racing-green */}
    <div
      aria-hidden="true"
      className="h-12 md:h-20 w-full relative"
      style={{
        background:
          'linear-gradient(to bottom, var(--pd-cream) 0%, #a8aa9c 25%, #2a3f33 65%, var(--pd-bg) 100%)',
      }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 top-[38%] h-px w-20 md:w-28 bg-[color:var(--pd-gold)]/55" />
    </div>
  </div>
);

export default HowItWorksPage;
